import React, { useMemo } from 'react';
import { useNewsListing } from 'volto-ministerio-publico-federal/hooks';
import NewsHeaderCard from '../NewsFolderView/NewsHeaderCard';
import NewsContext from '../NewsFolderView/NewsContext';
import NewsItemCard from '../NewsFolderView/NewsItemCard';
import PortalPagination from 'volto-ministerio-publico-federal/components/Pagination';
import CollectionText from '../CollectionText';
import {
  getRichTextData,
  hasOpeningText,
  isEyebrowText,
  toPlainText,
} from 'volto-ministerio-publico-federal/components/View/NewsFolderView/openingText';

type Content = Record<string, unknown>;

interface NewsGridViewProps {
  content: Content;
}

export const NewsGridView: React.FC<NewsGridViewProps> = ({ content }) => {
  const {
    result,
    loading,
    isCollection,
    effectiveCurrentPage,
    effectivePageSize,
    totalPages,
    handleQueryPaginationChange,
  } = useNewsListing(content);

  // Mesma regra do AllNews: texto curto vira tarja do cabeçalho, texto
  // rico segue para o corpo.
  const openingHtml = getRichTextData(content as { text?: { data?: string } });
  const eyebrowFromText = isEyebrowText(openingHtml)
    ? toPlainText(openingHtml)
    : undefined;
  const hasContentText = hasOpeningText(openingHtml) && !eyebrowFromText;

  // O NewsHeaderCard traz o h1 da página, mas ele só aparece quando não há
  // texto de abertura. Com texto, quem renderiza é o CollectionText — HTML do
  // editor, sem heading garantido — então o h1 vai visualmente oculto.
  const pageTitle = isCollection
    ? (content?.title as string) || 'Notícias'
    : 'Notícias';

  const contextValue = useMemo(
    () => ({
      currentPage: effectiveCurrentPage,
      setParameters: () => {},
    }),
    [effectiveCurrentPage],
  );

  return (
    <NewsContext.Provider value={contextValue}>
      <section className="news-grid-view" id="news-grid-view">
        <>
          {!hasContentText && (
            <NewsHeaderCard
              eyebrow={eyebrowFromText}
              title={pageTitle}
              description={
                isCollection
                  ? (content?.description as string) ||
                    'Acompanhe as publicações mais recentes.'
                  : 'Navegue por nossa galeria de notícias.'
              }
            />
          )}

          {hasContentText && (
            <>
              <h1 className="sr-only">{pageTitle}</h1>
              <CollectionText content={content} />
            </>
          )}

          {loading && (
            <div className="flex justify-center py-12">
              <span className="mpf-loading mpf-loading-spinner mpf-loading-lg text-primary dark:text-night-100" />
            </div>
          )}

          {!loading && (
            <>
              {result?.items?.length === 0 && (
                <div className="py-12 text-center">
                  <p className="text-gray-500">Nenhum resultado encontrado.</p>
                </div>
              )}

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {result?.items?.map((item) => (
                  <NewsItemCard item={item} key={item['@id']} variant="grid" />
                ))}
              </div>

              {totalPages > 1 && (
                <div className="mt-8 flex justify-center">
                  <PortalPagination
                    batching={result?.batching}
                    currentPage={effectiveCurrentPage}
                    // Total de ITENS: o `PortalPagination` é quem divide pelo
                    // tamanho da página.
                    totalItems={result?.items_total ?? 0}
                    defaultPageSize={effectivePageSize}
                    onPaginationChange={handleQueryPaginationChange}
                  />
                </div>
              )}
            </>
          )}
        </>
      </section>
    </NewsContext.Provider>
  );
};

export default NewsGridView;
